// Image Generation Page
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { generateImage, downloadBlob, createPreviewUrl } from '../services/aiMediaApi';
import { useSubscription } from '../components/SubscriptionContext';
import Paywall from '../components/Paywall';
import './ImageGeneration.css';

const ImageGeneration = () => {
  const featureKey = 'prompt-services';
  const { isFeatureUnlocked, subscribeForFeature, paymentLink } = useSubscription();
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Generating...');
  const [error, setError] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [progress, setProgress] = useState(0);

  const examplePrompts = [
    'A cat sitting on two girls',
    'Sunset over a mountain lake',
    'Futuristic city with flying cars',
    'A magical forest with glowing mushrooms',
    'Abstract art with vibrant colors'
  ];

  const handleGenerate = async (e) => {
    e.preventDefault();

    if (!isFeatureUnlocked(featureKey)) {
      setError('Please unlock image generation (₹5 per image) to continue.');
      return;
    }

    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setLoading(true);
    setError(null);
    setShowModal(false);
    setProgress(0);
    
    // Clear previous image
    if (imageUrl) {
      window.URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
    }
    setGeneratedImage(null);

    try {
      const imageBlob = await generateImage(prompt);
      setGeneratedImage(imageBlob);
      
      // Create preview URL
      const url = createPreviewUrl(imageBlob);
      setImageUrl(url);
      setShowModal(true);
      setProgress(100);
      toast.success('Image generated successfully');

      // Track usage for dashboard overview
      const prev = Number(localStorage.getItem('usage_images') || 0);
      localStorage.setItem('usage_images', String(prev + 1));
    } catch (err) {
      setError(err.message || 'Failed to generate image. Please try again.');
      toast.error(err.message || 'Failed to generate image. Please try again.');
      console.error('Generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (generatedImage) {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      downloadBlob(generatedImage, `generated-image-${timestamp}.png`);
    }
  };

  const handleExampleClick = (examplePrompt) => {
    setPrompt(examplePrompt);
  };

  const handleUnlock = () => {
    subscribeForFeature(featureKey);
  };

  // Loading message rotation
  useEffect(() => {
    const steps = [
      'Thinking about your image... 🎨',
      'Sketching the scene in my head...',
      'Creating a unique image for you...',
      'Adding lighting and color...',
      'Polishing details — bear with us ✨',
      'Rendering high-res pixels...',
      'Balancing contrast and tone...',
      'Final touches incoming...',
      'Almost done, preparing download...',
      'One more pass for quality...'
    ];
    if (!loading) return undefined;
    let idx = 0;
    setLoadingMessage(steps[0]);
    const id = setInterval(() => {
      idx = (idx + 1) % steps.length;
      setLoadingMessage(steps[idx]);
    }, 1200);
    return () => clearInterval(id);
  }, [loading]);

  // Synthetic progress to reassure during long waits
  useEffect(() => {
    if (!loading) {
      setProgress(0);
      return undefined;
    }
    let val = 0;
    setProgress(0);
    const id = setInterval(() => {
      val = Math.min(95, val + 5);
      setProgress(val);
    }, 600);
    return () => clearInterval(id);
  }, [loading]);

  // Cleanup URL on unmount
  useEffect(() => {
    return () => {
      if (imageUrl) {
        window.URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  return (
    <div className="image-generation-container">
      <div className="image-generation-card">
        <div className="image-generation-header">
          <h1>🎨 AI Image Generator</h1>
          <p>AI Services · Text to Image · Transform your ideas into stunning visuals</p>
        </div>

        {!isFeatureUnlocked(featureKey) && (
          <div style={{ marginBottom: '1.5rem' }}>
            <Paywall
              title="Unlock Prompt Services"
              description="One-time unlock for prompt-based image generation."
              priceLabel="₹5"
              ctaLabel="Pay ₹5 to unlock"
              onSubscribe={handleUnlock}
              paymentLink={paymentLink}
              features={[
                'Lifetime access after unlock',
                'Generate and download images from prompts',
                'Razorpay-secured payment',
              ]}
              showUnlockHint
            />
          </div>
        )}

        <form onSubmit={handleGenerate} className="generation-form">
          <div className="prompt-input-group">
            <label htmlFor="prompt">Describe your image</label>
            <textarea
              id="prompt"
              className="prompt-textarea"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Enter a detailed description of the image you want to generate..."
              disabled={loading}
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className={`generate-btn ${loading ? 'loading' : ''}`}
            disabled={loading || !isFeatureUnlocked(featureKey)}
          >
            {loading ? (
              <>
                <span className="btn-spinner"></span>
                Generating...
              </>
            ) : (
              'Generate Image'
            )}
          </button>

          <div className="prompt-examples">
            <h3>Try these examples:</h3>
            <div className="example-prompts">
              {examplePrompts.map((example, index) => (
                <button
                  key={index}
                  type="button"
                  className="example-prompt"
                  onClick={() => handleExampleClick(example)}
                  disabled={loading}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </form>
      </div>

      {loading && (
        <div className="fullscreen-overlay" role="status" aria-live="polite">
          <div className="overlay-content">
            <div className="following-stars" aria-hidden="true">
              <span></span><span></span><span></span><span></span>
            </div>
            <div className="star-icon" aria-hidden="true">★</div>
            <p className="overlay-main">{loadingMessage}</p>
            <p className="overlay-sub">We are crafting something unique. Please stay with us.</p>
            <div className="loading-dots" aria-hidden="true"><span></span><span></span><span></span></div>
            <div className="progress-shell" aria-hidden="true">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <p className="progress-text">Optimizing output...... {Math.min(progress, 100)}%</p>
          </div>
        </div>
      )}

      {showModal && imageUrl && (
        <div className="fullscreen-overlay result" role="dialog" aria-modal="true" onClick={() => setShowModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close left" onClick={() => setShowModal(false)} aria-label="Close">
              ✕
            </button>
            <div className="modal-image-wrap">
              <img src={imageUrl} alt="Generated" className="generated-image" />
              <button 
                onClick={handleDownload}
                className="download-floating"
                aria-label="Download generated image"
              >
                ⬇️
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGeneration;
